/**
 * Home feed — interactive card effects.
 *
 * For every `.feed-card` inside `#homeFeed` we:
 *   - inject a `.glare` overlay (a soft spotlight that tracks the pointer);
 *   - on `mousemove` / `touchmove`, rotate the card on X+Y axes for a 3D tilt
 *     and move the glare to follow the pointer;
 *   - on leave/end, reset the transform (the 400ms CSS transition handles the
 *     smooth settle-back);
 *   - inject a `.live-dot` (pulsing indicator) on cards whose image marks
 *     them as "live data" cards — currently the EV-charging-stations card
 *     and the weather card.
 *
 * The module is idempotent: each card is tagged with `data-tilt-bound="1"`
 * so re-running `init()` (e.g. after the home feed re-renders) is safe.
 *
 * Wired in `src/app/bootstrap.js`. No imports from legacy code — we just
 * listen to the DOM.
 */

import { createLogger } from '../../core/logger.js';

const log = createLogger('cardEffects');

const FEED_ID = 'homeFeed';
const MAX_TILT_DEG = 9;       // max rotation on each axis
const HOVER_LIFT_PX = 2;       // small upward translate while hovered

/**
 * Cards that ship real-time data get a pulsing green dot. We detect them
 * from the image URL (encoded Hebrew) instead of requiring the legacy card
 * factories to opt in. Substrings stay encoded so we don't have to depend
 * on the runtime decoding of the src attribute.
 */
const LIVE_CARD_IMAGE_FRAGMENTS = [
  '%D7%98%D7%A2%D7%99%D7%A0%D7%94',          // "טעינה"  — charging stations
  '%D7%9E%D7%96%D7%92%20%D7%90%D7%95%D7%95%D7%99%D7%A8', // "מזג אוויר"
];

let initialised = false;

/** Attach effects to existing + future `.feed-card` elements. Idempotent. */
export function init() {
  if (initialised) return;
  initialised = true;

  const wireExisting = () => {
    document.querySelectorAll(`#${FEED_ID} .feed-card`).forEach(enhanceCard);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      wireExisting();
      observeFeed();
    }, { once: true });
  } else {
    wireExisting();
    observeFeed();
  }
}

// ====================================================================
// internals
// ====================================================================

function observeFeed() {
  const feed = document.getElementById(FEED_ID);
  if (!feed) {
    // Home feed may not exist yet (e.g. user is still on the login screen).
    // Re-try when it shows up.
    const docObs = new MutationObserver(() => {
      const f = document.getElementById(FEED_ID);
      if (f) {
        docObs.disconnect();
        f.querySelectorAll('.feed-card').forEach(enhanceCard);
        observeFeedContents(f);
      }
    });
    docObs.observe(document.body, { childList: true, subtree: true });
    return;
  }
  observeFeedContents(feed);
}

/** @param {HTMLElement} feed */
function observeFeedContents(feed) {
  const obs = new MutationObserver((muts) => {
    for (const m of muts) {
      m.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.classList.contains('feed-card')) enhanceCard(node);
        node.querySelectorAll?.('.feed-card').forEach(enhanceCard);
      });
    }
  });
  obs.observe(feed, { childList: true, subtree: true });
}

/** @param {Element} _card */
function enhanceCard(_card) {
  const card = /** @type {HTMLElement} */ (_card);
  if (card.getAttribute('data-tilt-bound') === '1') return;
  card.setAttribute('data-tilt-bound', '1');

  injectGlare(card);
  maybeInjectLiveDot(card);
  attachTilt(card);
}

/** @param {HTMLElement} card */
function injectGlare(card) {
  if (card.querySelector(':scope > .glare')) return;
  const glare = document.createElement('div');
  glare.className = 'glare';
  card.prepend(glare);
}

/** @param {HTMLElement} card */
function maybeInjectLiveDot(card) {
  if (card.querySelector(':scope > .live-dot')) return;
  const img = card.querySelector('.thumb img');
  if (!img) return;
  const src = img.getAttribute('src') || '';
  const isLive = LIVE_CARD_IMAGE_FRAGMENTS.some((f) => src.includes(f));
  if (!isLive) return;
  const dot = document.createElement('div');
  dot.className = 'live-dot';
  dot.setAttribute('title', 'נתונים חיים');
  dot.setAttribute('aria-label', 'נתונים חיים');
  card.prepend(dot);
}

/** @param {HTMLElement} card */
function attachTilt(card) {
  /** @type {number | null} */
  let raf = null;

  const setTransform = (clientX, clientY) => {
    const rect = card.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const px = (clientX - rect.left) / rect.width;   // 0..1
    const py = (clientY - rect.top)  / rect.height;  // 0..1

    const tiltY = (px - 0.5) *  MAX_TILT_DEG * 2;    // pointer left↔right
    const tiltX = (py - 0.5) * -MAX_TILT_DEG * 2;    // pointer up↔down

    if (raf !== null) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => {
      card.style.transform =
        `perspective(900px) rotateX(${tiltX.toFixed(2)}deg) ` +
        `rotateY(${tiltY.toFixed(2)}deg) translateY(-${HOVER_LIFT_PX}px)`;
      card.style.setProperty('--glare-x', (px * 100).toFixed(1) + '%');
      card.style.setProperty('--glare-y', (py * 100).toFixed(1) + '%');
    });
  };

  const reset = () => {
    if (raf !== null) { cancelAnimationFrame(raf); raf = null; }
    card.classList.remove('tilting');
    card.style.transform = '';
  };

  card.addEventListener('mouseenter', () => card.classList.add('tilting'));
  card.addEventListener('mousemove', (e) => setTransform(e.clientX, e.clientY));
  card.addEventListener('mouseleave', reset);

  card.addEventListener('touchstart', (e) => {
    card.classList.add('tilting');
    const t = e.touches[0];
    if (t) setTransform(t.clientX, t.clientY);
  }, { passive: true });
  card.addEventListener('touchmove', (e) => {
    const t = e.touches[0];
    if (t) setTransform(t.clientX, t.clientY);
  }, { passive: true });
  card.addEventListener('touchend', reset);
  card.addEventListener('touchcancel', reset);

  void log; // keep import-side-effects intact even if logging is silenced
}
